import compact from "lodash/compact"
import { createRouter, createWebHistory } from "vue-router"

import bus from "~/bus"
import store from "~/store"

import { combineNavigationGuards, logoutRedirection } from "~/lib/vueUtils"
import { forwardToNextPage } from "~/modules/login/utils"

import {
    createAllRoutesGuard,
    createCommunicationGuard,
    createDataPolicyGuard,
    createInitialSetupGuard,
    createProfileGuard,
    createPublicRoutesGuard,
    createSpacesGuard,
} from "~/modules/login/navigationGuards"

import {
    buildSupervisorHasGuard,
    buildSupervisorRequestIsAvailableGuard,
    buildSupervisorSectionIsAvailableGuard,
} from "~/modules/supervisor/navigationGuards"

/**
 * Available meta properties:
 * {
 *      public: Boolean           Don't require current user
 * }
 */
const routes = compact([
    {
        name: "home",
        path: "/",
        redirect() {
            const { currentUser, defaultEntryPoint } = store.getters
            if (!currentUser) return "/login"
            return defaultEntryPoint?.homePage ?? "/profile"
        },
    },

    {
        name: "calendar",
        path: "/calendar",
        component: () => import("~/modules/calendar/views/CalendarView"),
        props: route => ({ startDay: route.query.day }),
        meta: {
            hasChatbot: true,
        },
    },

    {
        name: "faqs",
        path: "/faqs",
        component: () => import("~/modules/faqs/views/FaqsView"),
        meta: {
            hasChatbot: true,
        },
    },

    {
        name: "profile",
        path: "/profile",
        redirect: "/profile/dossier",
        meta: {
            hasChatbot: false,
        },
        component: () => import("~/modules/profile/views/ProfileView"),
        children: compact([
            {
                path: "dossier",
                component: () =>
                    import("~/modules/profile/views/tabs/DossierTab"),
            },
            {
                path: "training",
                component: () =>
                    import("~/modules/profile/views/tabs/TrainingTab"),
            },
            {
                path: "resume",
                component: () =>
                    import("~/modules/profile/views/tabs/ResumeTab"),
            },
            {
                path: "external-training",
                component: () =>
                    import("~/modules/profile/views/tabs/ExternalTrainingTab"),
            },
            {
                path: "settings",
                component: () =>
                    import("~/modules/profile/views/tabs/SettingsTab"),
            },
            {
                path: "credits",
                component: () =>
                    import("~/modules/profile/views/CreditsDashboard"),
            },
            {
                path: "data-policy",
                component: () =>
                    import("~/modules/profile/views/tabs/DataPolicyTab"),
            },
        ]),
        beforeEnter: createProfileGuard(store),
    },

    {
        name: "tools",
        path: "/slo",
        redirect: "/slo/tools",
        component: () => import("~/modules/slotools/views/SloToolsView"),
        children: [
            {
                path: "tools",
                component: () =>
                    import("~/modules/slotools/views/tabs/ToolsTab"),
            },
            {
                path: "executions",
                component: () =>
                    import("~/modules/slotools/views/tabs/ExecutionsTab"),
            },
        ],
    },

    {
        path: "/slo/tools/:toolId(\\d+)",
        props: true,
        component: () => import("~/modules/slotools/views/ToolForm"),
    },

    {
        name: "reports",
        path: "/reports",
        redirect: "/reports/list",
        component: () => import("~/modules/v2reports/views/ReportsView"),
        children: [
            {
                path: "list",
                component: () =>
                    import("~/modules/v2reports/views/tabs/ReportsTab"),
            },
            {
                path: "executions",
                component: () =>
                    import("~/modules/v2reports/views/tabs/ExecutionsTab"),
            },
        ],
    },

    {
        path: "/reports/:reportId(\\d+)",
        props: true,
        component: () => import("~/modules/v2reports/views/ReportForm"),
    },

    {
        name: "login",
        path: "/login",
        component: () => import("~/modules/login/views/LoginView"),
        props: route => ({
            assign: route.query.assign,
            redirect: route.query.redirect,
            ssoUserError: route.query.ssoUserError,
            ssoErrorRequest: !!route.query.ssoErrorRequest?.trim(),
            ssoExpiredToken: route.query.ssoExpiredToken === "true",
        }),
        meta: { public: true },
        beforeEnter: createPublicRoutesGuard(store),
    },

    {
        name: "logout",
        path: "/logout",
        component: () => import("~/modules/login/views/LogoutView"),
        props: r => ({
            message: r.query.message ? decodeURI(r.query.message) : null,
            url: r.query.url ? decodeURI(r.query.url) : null,
        }),
        meta: { public: true },
        beforeEnter: createPublicRoutesGuard(store),
    },

    {
        name: "remember",
        path: "/remember",
        component: () => import("~/modules/login/views/RememberView"),
        props: route => ({ redirect: route.query.redirect }),
        meta: { public: true },
        beforeEnter: createPublicRoutesGuard(store),
    },

    {
        name: "register",
        path: "/register",
        component: () => import("~/modules/login/views/RegisterView"),
        props: route => ({ redirect: route.query.redirect }),
        meta: { public: true },
        beforeEnter: createPublicRoutesGuard(store),
    },

    {
        name: "reset-credentials",
        path: "/reset-credentials",
        component: () => import("~/modules/login/views/ResetCredentialsView"),
        props: route => ({
            redirect: route.query.redirect,
            token: route.query.token,
        }),
        meta: { public: true },
        beforeEnter: async (to, from, next) => {
            if (store.getters.currentUser) {
                await store.dispatch("logoutNoRedirect")
            }
            return createPublicRoutesGuard(store)(to, from, next)
        },
    },

    {
        name: "2fa",
        path: "/2fa",
        component: () =>
            import("~/modules/login/views/AuthenticationStep1View"),
        props: route => ({
            assign: route.query.assign,
            redirect: route.query.redirect,
            token: route.query.token,
            userId: Number(route.query.user),
        }),
        meta: { public: true },
        beforeEnter: createPublicRoutesGuard(store),
    },

    {
        name: "2fa-send",
        path: "/2fa-send",
        component: () =>
            import("~/modules/login/views/AuthenticationStep2View"),
        props: route => ({
            assign: route.query.assign,
            option: route.query.option,
            redirect: route.query.redirect,
            token: route.query.token,
            userId: Number(route.query.user),
        }),
        meta: { public: true },
        beforeEnter: createPublicRoutesGuard(store),
    },

    {
        name: "initial-setup",
        path: "/initial-setup",
        component: () => import("~/modules/login/views/InitialSetupView"),
        props: route => ({
            assign: route.query.assign,
            redirect: route.query.redirect,
        }),
        meta: { public: true },
        beforeEnter: createInitialSetupGuard(store),
    },

    {
        name: "data-policy",
        path: "/data-policy",
        component: () => import("~/modules/login/views/DataPolicyView"),
        props: route => ({
            assign: route.query.assign,
            redirect: route.query.redirect,
        }),
        meta: { public: false },
        beforeEnter: createDataPolicyGuard(store),
    },

    {
        name: "communication",
        path: "/communication/:channelId?",
        meta: {
            canHighlightSpace: true,
            hasChatbot: true,
        },
        props: r => ({
            channelId: r.params.channelId ? Number(r.params.channelId) : null,
        }),
        component: () =>
            import("~/modules/communication/views/CommunicationView"),
        beforeEnter: createCommunicationGuard(store),
    },

    {
        name: "space",
        path: "/space/:spaceId",
        component: () => import("~/modules/spaces/views/SpaceView"),
        props: r => {
            return {
                spaceId: r.params.spaceId,
                support: Object.hasOwn(r.query, "support"),
            }
        },
        meta: {
            canHighlightSpace: true,
            hasChatbot: true,
        },
        beforeEnter: createSpacesGuard(store),
    },

    {
        name: "bookmarks",
        path: "/bookmarks",
        component: () => import("~/modules/bookmarks/views/BookmarksView"),
        meta: {
            hasChatbot: true,
        },
    },

    {
        name: "list",
        path: "/list/:listId",
        meta: {
            canHighlightSpace: true,
            hasChatbot: true,
        },
        component: () => import("~/modules/list/views/ListView"),
        props: r => ({
            ...r.params,
            parents: r.query.parents,
            filters: r.query.filters,
        }),
    },

    {
        path: "/call/:userCallId(\\d+)",
        redirect: to => ({
            path: `/call/${to.params.userCallId}/information`,
        }),
    },

    {
        name: "call",
        path: "/call/:userCallId(\\d+)/:currSection(information|activities|forums|resources|virtual-classrooms|classmates|teachers|calendar)",
        component: () => import("~/modules/calls/views/CallView"),
        props: r => ({
            userCallId: Number(r.params.userCallId),
            currSection: r.params.currSection,
            userActivityId: r.query?.activity ? Number(r.query.activity) : null,
        }),
        meta: {
            canHighlightSpace: true,
        },
    },

    {
        name: "virtualClassroom",
        path: "/call/:userCallId(\\d+)/vc/:vcId(\\d+)",
        component: () => import("~/modules/calls/views/VirtualClassroomView"),
        props: r => ({
            userCallId: Number(r.params.userCallId),
            vcId: Number(r.params.vcId),
        }),
    },

    {
        name: "news",
        path: "/news/:newsItemId(\\d+)",
        meta: {
            canHighlightSpace: true,
            hasChatbot: true,
        },
        component: () => import("~/modules/news/views/NewsItemView"),
        props: true,
    },

    {
        name: "plugin",
        path: "/plugin/:userActivityId(\\d+)",
        component: () => import("~/modules/plugins/views/PluginView"),
        props: r => ({
            userActivityId: r.params.userActivityId,
            activityId: Number(r.query.chapter),
        }),
    },

    {
        name: "resource",
        path: "/resources/:resourceId(\\d+)",
        meta: {
            canHighlightSpace: true,
            hasChatbot: true,
        },
        component: () => import("~/modules/resources/views/ResourceView"),
        props: true,
    },

    {
        name: "program",
        path: "/program/:programId(\\d+)",
        meta: {
            canHighlightSpace: true,
            hasChatbot: true,
        },
        component: () => import("~/modules/programs/views/ProgramView"),
        props: true,
    },

    {
        name: "messages",
        path: "/messages/:folder?/:messageUserId?",
        component: () => import("~/modules/messages/views/MessagesView"),
        props: r => ({
            folder: r.params.folder,
            messageUserId: r.params.messageUserId,
        }),
        meta: {
            hasChatbot: true,
        },
    },

    {
        name: "post",
        path: "/post/:postId(\\d+)",
        meta: {
            canHighlightSpace: true,
            hasChatbot: true,
        },
        component: () => import("~/modules/communication/views/PostView"),
        props: r => ({
            postId: Number(r.params.postId),
        }),
    },

    {
        name: "supervisor",
        path: "/supervisor",
        redirect() {
            return (
                store.state.supervisor.panel?.supervisorUrl ?? {
                    name: "login",
                    query: {
                        redirect: "/supervisor",
                    },
                }
            )
        },
        component: () => import("~/modules/supervisor/views/SupervisorView"),
        children: [
            // Redirect to first available request type
            {
                path: "requests",
                beforeEnter: (to, from, next) => {
                    if (store.getters["supervisor/hasRequests"]) {
                        next()
                    } else {
                        next({ name: "not-found" })
                    }
                },
                redirect() {
                    const availableRequests =
                        store.getters["supervisor/availableRequestLinks"]
                    return `/supervisor/requests/${availableRequests[0]}`
                },
            },

            // Pending Requests
            {
                path: "requests/lms",
                beforeEnter: buildSupervisorHasGuard(
                    store,
                    "hasFormationRequests"
                ),
                name: "lmsRequestsList",
                component: () =>
                    import("~/modules/supervisor/views/LmsRequestsView"),
            },
            {
                path: "requests/external",
                beforeEnter: buildSupervisorHasGuard(
                    store,
                    "hasExternalFormationRequests"
                ),
                name: "externalRequestsList",
                component: () =>
                    import("~/modules/supervisor/views/ExternalRequestsView"),
            },
            {
                path: "requests/experience",
                beforeEnter: buildSupervisorHasGuard(
                    store,
                    "hasExternalExperienceRequests"
                ),
                name: "experienceRequestsList",
                component: () =>
                    import("~/modules/supervisor/views/ExperienceRequestsView"),
            },
            {
                path: "requests/preregister",
                beforeEnter: buildSupervisorHasGuard(
                    store,
                    "hasPreregisterRequests"
                ),
                name: "preregisterRequestsList",
                component: () =>
                    import(
                        "~/modules/supervisor/views/PreregisterRequestsView"
                    ),
            },

            // Historical requests
            {
                path: "requests/lms/historical",
                beforeEnter: buildSupervisorHasGuard(
                    store,
                    "hasFormationRequests"
                ),
                name: "historicalLmsRequestsList",
                component: () =>
                    import(
                        "~/modules/supervisor/views/HistoricalLmsRequestsView"
                    ),
            },
            {
                path: "requests/experience/historical",
                beforeEnter: buildSupervisorHasGuard(
                    store,
                    "hasExternalExperienceRequests"
                ),
                name: "historicalExperienceRequests",
                component: () =>
                    import(
                        "~/modules/supervisor/views/HistoricalExperienceRequestsView"
                    ),
            },
            {
                path: "requests/external/historical",
                beforeEnter: buildSupervisorHasGuard(
                    store,
                    "hasExternalFormationRequests"
                ),
                name: "historicalExternalRequests",
                component: () =>
                    import(
                        "~/modules/supervisor/views/HistoricalExternalRequestsView"
                    ),
            },
            {
                path: "requests/preregister/historical",
                beforeEnter: buildSupervisorHasGuard(
                    store,
                    "hasPreregisterRequests"
                ),
                name: "historicalPreregisterRequests",
                component: () =>
                    import(
                        "~/modules/supervisor/views/HistoricalPreregisterRequestsView"
                    ),
            },

            // TODO Old historical requests redirections
            {
                path: "requests/historical/lms",
                redirect: () => "/supervisor/requests/lms/historical",
            },
            {
                path: "requests/historical/experience",
                redirect: () => "/supervisor/requests/experience/historical",
            },
            {
                path: "requests/historical/external",
                redirect: () => "/supervisor/requests/external/historical",
            },
            {
                path: "requests/historical/preregister",
                redirect: () => "/supervisor/requests/preregister/historical",
            },

            {
                path: "requests/:requestType(lms|external|experience|preregister)/:historical(historical)?/:requestId(\\d+)",
                name: "requestDetail",
                beforeEnter: combineNavigationGuards([
                    buildSupervisorSectionIsAvailableGuard(store),
                    buildSupervisorRequestIsAvailableGuard(store),
                ]),
                component: () =>
                    import("~/modules/supervisor/views/RequestDetailView"),
                props: r => ({
                    requestId: Number(r.params.requestId),
                    requestType: r.params.requestType,
                    historical: !!r.params.historical,
                }),
            },

            {
                path: "dashboard",
                name: "dashboard",
                beforeEnter: buildSupervisorSectionIsAvailableGuard(store),
                component: () =>
                    import("~/modules/supervisor/views/DashboardView"),
            },
            {
                path: "courses",
                name: "courses",
                redirect: {
                    name: "coursesSection",
                    params: { currSection: "all" },
                },
                beforeEnter: buildSupervisorSectionIsAvailableGuard(store),
                component: () =>
                    import("~/modules/supervisor/views/CoursesView"),
                children: [
                    {
                        name: "coursesSection",
                        component: () =>
                            import("~/modules/supervisor/views/CoursesView"),
                        path: ":currSection(all|participants|catalog)",
                    },
                ],
                props: r => ({
                    currSection: r.params.currSection,
                }),
            },
            {
                path: "courses/:programId(\\d+)",
                name: "courseDetail",
                beforeEnter: buildSupervisorSectionIsAvailableGuard(store),
                component: () =>
                    import("~/modules/supervisor/views/CourseDetailView"),
                props: r => ({
                    programId: Number(r.params.programId),
                }),
            },
            {
                path: "participants/:userId(\\d+)",
                name: "participantDetail",
                beforeEnter: buildSupervisorSectionIsAvailableGuard(store),
                component: () =>
                    import("~/modules/supervisor/views/ParticipantDetailView"),
                props: r => ({
                    userId: Number(r.params.userId),
                }),
            },
            {
                path: "participants",
                name: "participants",
                beforeEnter: buildSupervisorSectionIsAvailableGuard(store),
                component: () =>
                    import("~/modules/supervisor/views/ParticipantsView"),
            },
            {
                path: "messages",
                name: "supervisorMessages",
                redirect: "messages/inbox",
                beforeEnter: buildSupervisorSectionIsAvailableGuard(store),
            },
            {
                path: "messages/:folder?/:messageUserId?",
                name: "supervisorMessageDetail",
                beforeEnter: buildSupervisorSectionIsAvailableGuard(store),
                component: () =>
                    import("~/modules/supervisor/views/MessagesView"),
                props: r => ({
                    folder: r.params.folder,
                    messageUserId: r.params.messageUserId,
                }),
            },
        ],
    },

    {
        name: "ranking",
        path: "/ranking",
        meta: {
            canHighlightSpace: true,
            hasChatbot: true,
        },
        component: () => import("~/modules/ranking/views/RankingView"),
    },

    {
        name: "bi-dashboard",
        path: "/bi-dashboard",
        component: () => import("~/modules/bi/views/DashboardView"),
    },

    {
        name: "not-found",
        path: "/:pathMatch(.*)*",
        component: () => import("~/NotFound"),
        meta: { public: true },
    },
])

const vueRouterOptions = {
    history: createWebHistory(import.meta.env.VITE_CLIENT_BASE_PATH),
    routes,
    scrollBehavior(to, _from, savedPosition) {
        if (savedPosition) return savedPosition

        const lastNavigationToThisPage = store.state.navigation
            .filter(navigation => {
                return navigation.from.path == to.path
            })
            .at(-1)
        if (lastNavigationToThisPage) {
            return lastNavigationToThisPage.savedPosition
        }

        return { left: 0, top: 0 }
    },
}

Object.defineProperty(vueRouterOptions, "linkActiveClass", {
    get() {
        // We can change this to `store.getters["theme/styles"].linkPrimary` when (and if) we want
        return ""
    },
})

Object.defineProperty(vueRouterOptions, "linkExactActiveClass", {
    get() {
        // We can change this to `store.getters["theme/styles"].linkPrimary` when (and if) we want
        return ""
    },
})

const router = createRouter(vueRouterOptions)

router.beforeEach(createAllRoutesGuard(store))

/**
 * When the user wants to go to a page that requires to be logged in
 * they get redirected to the login page with the desired destination
 * as the redirect param.
 */
bus.on("auth-error", ({ type, message, url } = {}) => {
    const route = router.currentRoute.value

    store.dispatch("clearStore").then(() => {
        if (route.name === "login") return

        store.commit("setBearerToken", undefined)
        logoutRedirection({
            type,
            message,
            url,
            router,
            query: { redirect: route.fullPath },
        })
    })
})

bus.on("404-error", () => {
    router.push({ name: "not-found" })
})

bus.on("405-error", () => {
    const { defaultEntryPoint: entryPoint } = store.getters
    forwardToNextPage({ entryPoint, router })
})

export default router
